import React, { useRef, useMemo, useCallback } from 'react';

import { Popper, Text } from '@fragments';

import './styles.scss';

/**
 * Menu — an ergonomic, declarative dropdown built on top of the Popper fragment.
 *
 * Instead of managing a controlRef and calling `.open(options, side, anchorEl)`
 * yourself, you declare your `items` up front and hand Menu a single `trigger`
 * child. Menu clones the trigger to wire up the open behavior (click, or hover
 * when `activateOnHover` is set), builds the Popper option list from `items`,
 * and positions the menu off the trigger element.
 *
 * @example
 * <Menu
 *   items={[
 *     { label: 'Editar', icon: <IconPencil />, onClick: onEdit },
 *     { divider: true },
 *     { label: 'Excluir', icon: <IconTrash />, variant: 'error', onClick: onDelete },
 *   ]}
 * >
 *   <Button icon={<IconDots />} color="overlay" variant="text" />
 * </Menu>
 */
export default function Menu({
    items = [],
    children,
    placement = 'bottom-start',
    search = false,
    activateOnHover = false,
    header = null,
    onOpenChange,
    config: extraConfig = {},
}) {
    const popperRef = useRef(null);

    // Map declarative `items` into the option shape Popper expects. `shortcut`
    // becomes a small kbd chip on the right (unless `right` is already given),
    // and any falsy/empty entries are dropped so callers can conditionally
    // include items with `condition && {...}`.
    const buildOptions = useCallback(() => {
        return items
            .filter(Boolean)
            .map((item, index) => {
                if (item.divider) {
                    return { divider: true, id: item.id ?? `divider-${index}` };
                }

                const right =
                    item.right ??
                    (item.shortcut ? (
                        <Text.caption contrast={6} className="menu-kbd">
                            {item.shortcut}
                        </Text.caption>
                    ) : undefined);

                return {
                    id: item.id ?? `menu-item-${index}`,
                    label: item.label,
                    icon: item.icon,
                    variant: item.variant || 'default',
                    disabled: item.disabled,
                    active: item.active,
                    onClick: item.onClick,
                    right,
                };
            });
    }, [items]);

    // Match the typed query against the option label. Popper calls this with
    // (option, validate) where `validate(value)` returns true if `value`
    // normalizes to a match for the current query.
    const searchKeyExtractor = useMemo(() => {
        if (!search) return null;
        return (option, validate) => validate(option.label);
    }, [search]);

    const config = useMemo(
        () => ({
            activateOnHover,
            searchKeyExtractor,
            ...extraConfig,
        }),
        [activateOnHover, searchKeyExtractor, extraConfig]
    );

    const openMenu = useCallback(
        (anchorEl) => {
            if (!anchorEl) return;
            popperRef.current?.open(buildOptions(), placement, anchorEl, header);
        },
        [buildOptions, placement, header]
    );

    // Clone the single trigger child to attach the open handlers while
    // preserving any handlers the caller already declared on it.
    const trigger = useMemo(() => {
        if (!React.isValidElement(children)) return children;

        return React.cloneElement(children, {
            onClick: (event) => {
                children.props.onClick?.(event);
                if (event.defaultPrevented) return;
                openMenu(event.currentTarget);
            },
            onMouseEnter: activateOnHover
                ? (event) => {
                      children.props.onMouseEnter?.(event);
                      openMenu(event.currentTarget);
                  }
                : children.props.onMouseEnter,
        });
    }, [children, activateOnHover, openMenu]);

    return (
        <>
            {trigger}
            <Popper controlRef={popperRef} config={config} onOpenChange={onOpenChange} />
        </>
    );
}
